import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import { upsertFetcherObjects } from "../../../../features/fetcherSettingsSlice";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import {
  CSV_COLUMNS,
  DEV_OBJECTS_CSV_SAMPLE,
  ParsedCsv,
  parseDevObjectsCsv,
} from "../helpers/devObjectsCsv";
import {
  isStandardLevel,
  LEVEL_ORDER,
} from "../helpers/fetcherSettingsResolver";
import { nextLocalId } from "../helpers/localIds";
import { nowStamp } from "../helpers/formatters";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Bulk import for Dev Objects rows. Rows are written on import - nothing is
 * staged - and the endpoint rejects the whole batch if any row is invalid, so
 * the preview has to show every row's own verdict first.
 */
const ImportDevObjectsCsvModal = ({ open, onClose }: Props) => {
  const dispatch = useDispatch();
  const { objects } = useSelector((state: AppState) => state.fetcherSettings);
  const devObjects = objects.filter((row) => !isStandardLevel(row.raw));

  const [text, setText] = useState("");
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);

  useEffect(() => {
    if (!open) return;
    setText("");
    setParsed(null);
  }, [open]);

  const goodRows = parsed && !parsed.error
    ? parsed.rows.filter((row) => row.errors.length === 0)
    : [];
  const badRows = parsed && !parsed.error
    ? parsed.rows.filter((row) => row.errors.length > 0)
    : [];

  const downloadSample = () => {
    const link = document.createElement("a");
    link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(DEV_OBJECTS_CSV_SAMPLE)}`;
    link.download = "fetcher_objects_sample.csv";
    link.click();
  };

  const readFile = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result ?? "");
      setText(content);
      setParsed(parseDevObjectsCsv(content, devObjects));
    };
    reader.readAsText(file);
  };

  const handleImport = () => {
    // TODO: POST /aladdin_api/v1/data_collection/fetcher/objects/import/
    // all-or-nothing; any invalid row rejects the whole batch
    let nextId = Number(nextLocalId(objects));
    dispatch(
      upsertFetcherObjects(
        goodRows.map((row) => ({
          id: String(nextId++),
          lvl: LEVEL_ORDER.includes(row.object_type)
            ? row.object_type
            : "__bad__",
          raw: row.object_type,
          mid: row.object_value,
          k: row.field_type,
          v: row.field_value,
          ufr: "0",
          mod: nowStamp(),
        })),
      ),
    );
    dispatch(
      setDisplayErrorSnackBar({
        message: `${goodRows.length} row${goodRows.length === 1 ? "" : "s"} imported.`,
        type: "success",
      }),
    );
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: "700px", maxWidth: "94vw" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>Import rows from CSV</DialogTitle>
      <Typography variant="body2" sx={{ px: 3, pb: 1.5, color: "#00000099" }}>
        Rows are written to <code>objects</code> on import — nothing is staged.
      </Typography>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          The file must have a header row with exactly these four column names,
          in any order:
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
            {CSV_COLUMNS.map((column) => (
              <Chip key={column} size="small" label={column} />
            ))}
          </Stack>
          <Typography sx={{ fontSize: "12.5px", mt: 1 }}>
            Values containing a comma must be wrapped in double quotes.{" "}
            <code>id</code>, <code>created</code>, <code>modified</code> and the{" "}
            <code>_by</code> columns are set by the endpoint and must not be
            included.
          </Typography>
          <Button
            size="small"
            color="secondary"
            startIcon={<DownloadIcon />}
            sx={{ mt: 1 }}
            onClick={downloadSample}
          >
            Download sample CSV
          </Button>
        </Alert>

        <Stack spacing={1.5}>
          <Button component="label" color="secondary" variant="outlined">
            Choose CSV file
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => readFile(event.target.files?.[0])}
            />
          </Button>
          <TextField
            fullWidth
            multiline
            minRows={6}
            size="small"
            label="Or paste the rows"
            placeholder="object_type,object_value,field_type,field_value"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setParsed(null);
            }}
            color="secondary"
          />
          <Button
            size="small"
            color="secondary"
            sx={{ alignSelf: "flex-start" }}
            onClick={() => setParsed(parseDevObjectsCsv(text, devObjects))}
          >
            Validate
          </Button>
        </Stack>

        {parsed?.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {parsed.error}
          </Alert>
        )}

        {parsed && !parsed.error && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={badRows.length ? "warning" : "success"}>
              <b>{goodRows.length}</b> row
              {goodRows.length === 1 ? "" : "s"} ready to import
              {badRows.length ? ` · ${badRows.length} rejected` : ""}.
            </Alert>
            <Stack spacing={0.75} sx={{ mt: 1.5, maxHeight: 260, overflowY: "auto" }}>
              {parsed.rows.map((row) => (
                <Stack
                  key={row.line}
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{
                    border: "1px solid #0000001F",
                    borderRadius: "7px",
                    px: 1.5,
                    py: 1,
                    backgroundColor: row.errors.length
                      ? "#FBE9E9"
                      : "transparent",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "11.5px", color: "#00000099", width: 60 }}
                  >
                    line {row.line}
                  </Typography>
                  <Typography
                    className="fetcher-mono"
                    sx={{ fontSize: "12px", width: 190 }}
                  >
                    {row.object_type} / {row.object_value}
                  </Typography>
                  <Typography sx={{ fontSize: "12.5px", width: 150 }}>
                    {row.field_type}
                  </Typography>
                  <Typography
                    className="fetcher-mono"
                    sx={{ fontSize: "12px", flex: 1, wordBreak: "break-all" }}
                  >
                    {row.field_value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      width: 170,
                      color: row.errors.length
                        ? "#C0392B"
                        : row.warning
                          ? "#B45309"
                          : "#1E8E4E",
                    }}
                  >
                    {row.errors.length
                      ? row.errors.join("; ")
                      : (row.warning ?? "OK")}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={goodRows.length === 0}
          onClick={handleImport}
        >
          Import {goodRows.length || ""} row{goodRows.length === 1 ? "" : "s"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDevObjectsCsvModal;
